// The fixed tables of the DOM overlay: the menus and their sections, the
// label of each, the battle-plan pickers, the role words, the dev panel's
// ladders, and the rows `actButton` dispatches by. Each one is built once,
// when the module loads, so the picked language is already in every label
// (R855) and the 4 Hz regen pays for no table.

import { ChatChannel, FleetOp } from '../../gen/proto/aquaphobia';
import type { Resources } from '../../gen/proto/aquaphobia';
import { card as CS, overlay as S } from '../../gen/strings';
import type { FeatureFlag } from '../../gen/featureflags';
import type { AudioOp, PickMode, UIActions } from './types';


/**
 * The wire's deposit id for each commodity the top bar reads out, for the
 * explainer card on the cell (§12.0).
 *
 * `RESOURCE_NAMES` is indexed by deposit id and credits are not mined from
 * a deposit, so credits have no entry there and this table has no row for
 * them either: the card names them itself rather than indexing past the
 * end of a table.
 */
export const RES_DEPOSIT: Partial<Record<keyof Resources, number>> = {
  alloy: 1, biomass: 2, volatiles: 3, isotopes: 4,
};

/**
 * The player glossary term of each top-bar cell whose card names a
 * commodity (R1557): the name at the head of the explainer card is a term
 * button. The world clock names no single term, so it has no row.
 */
export const BAR_TERMS: Readonly<Record<string, string>> = {
  alloy: 'alloy', biomass: 'biomass', volatiles: 'volatiles', isotopes: 'isotopes',
  credits: 'credits', cores: 'data_core',
};

// The shell (ui-map-interface.md §12): six game menus on the tab bar (the
// thumb dock on a telephone), each with sub-menu pills. The meta screens
// (CLAN, STANDINGS, INBOX) open into the same sheet: on a desktop from the
// dock head (the INBOX cell, the rank chip, the squad card), on a telephone
// from the INBOX cap and the commander row of the OPTIONS menu.
export const GAME_TABS = ['BUILD', 'DRAFT', 'FLEET', 'CAPTAIN', 'ECONOMY', 'WAR'] as const;

const META_TABS = ['CLAN', 'STANDINGS', 'INBOX'] as const;

export type Tab = typeof GAME_TABS[number] | typeof META_TABS[number];

export const SUBS: Record<Tab, string[]> = {
  BUILD: ['BASE', 'TECH', 'SHIPYARD', 'TORPEDO'],
  DRAFT: ['INVENTION', 'MASTERS', 'PRINTS'],
  FLEET: ['MISSIONS', 'EXPEDITIONS'],
  CAPTAIN: ['WARDROOM', 'RECRUITMENT', 'PRISON'],
  ECONOMY: ['PRODUCTION', 'AUCTION', 'CASINO'],
  WAR: ['SOVEREIGNTY', 'STRUCTURES', 'INTEL', 'REPORTS'],
  CLAN: [], STANDINGS: [], INBOX: [],
};

/**
 * The pills that exist only while a v2 feature flag is on (mockup round
 * 088 cards 09, 14 and 15; v2-the-deep.md §17, R1811). Each one stands
 * after the pills of today, in this order. With every flag off, a menu
 * holds the pills of `SUBS` and no more.
 */
export const FLAG_SUBS: Partial<Record<Tab, readonly { sub: string; flag: FeatureFlag }[]>> = {
  BUILD: [{ sub: 'DEPOT', flag: 'sapper' }],
  // ECONOMY · TREASURE (R2700): the caches, the fragments and the finds.
  ECONOMY: [{ sub: 'TREASURE', flag: 'treasure' }],
  FLEET: [{ sub: 'COMMISSIONS', flag: 'commissions' }, { sub: 'ROUTES', flag: 'routes' }],
};

/**
 * The pills of one menu in this world: the pills of today, then each pill
 * of `FLAG_SUBS` whose flag the world latched (R1811). `has` is the
 * flag reader of the state (`GameState.hasFeatureFlag`). `noCasino`
 * takes the CASINO pill away: a store app hides the casino
 * (mobile-apps.md §8, R2927).
 */
export function subsOf(tab: Tab, has: (flag: FeatureFlag) => boolean, noCasino = false): string[] {
  const extra = (FLAG_SUBS[tab] ?? []).filter((x) => has(x.flag)).map((x) => x.sub);
  const subs = extra.length === 0 ? SUBS[tab] : [...SUBS[tab], ...extra];
  return noCasino && subs.includes('CASINO') ? subs.filter((x) => x !== 'CASINO') : subs;
}

export const ATTR_SHORT = S.attr_short;

/** The word each tab, sub-tab and auction page prints (R343): the key
 *  stays in the code, the label comes from the locale. */
const TAB_LABEL: Record<string, string> = {
  BUILD: S.tab_build, DRAFT: S.tab_draft, FLEET: S.tab_fleet,
  CAPTAIN: S.tab_captain, ECONOMY: S.tab_economy, WAR: S.tab_war,
  BASE: S.sub_base, TECH: S.sub_tech, SHIPYARD: S.sub_shipyard, TORPEDO: S.sub_torpedo,
  PRINTS: S.sub_prints, MASTERS: S.sub_masters,
  INVENTION: S.sub_invention,
  MISSIONS: S.sub_missions, EXPEDITIONS: S.sub_expeditions,
  DEPOT: S.sub_depot, COMMISSIONS: S.sub_commissions, ROUTES: S.sub_routes,
  WARDROOM: S.sub_wardroom, RECRUITMENT: S.sub_recruitment,
  PRISON: S.sub_prison,
  PRODUCTION: S.sub_production, AUCTION: S.sub_auction, CASINO: S.sub_casino, TREASURE: S.sub_treasure,
  SOVEREIGNTY: S.sub_sovereignty, STRUCTURES: S.sub_structures,
  INTEL: S.sub_intel, REPORTS: S.sub_reports,
  BROWSE: S.auc_browse, 'ADD OFFER': S.auc_add_offer,
};

export const tabLabel = (key: string): string => TAB_LABEL[key] ?? key;

/** The short word of each game tab (R1749): under its glyph on a
 *  telephone under GLYPH + WORD (R2215), and the first fall-back of a
 *  desktop tab whose full word does not fit the dock (R2214). */
const TAB_SHORT: Record<string, string> = {
  BUILD: S.tab_short_build, DRAFT: S.tab_short_draft, FLEET: S.tab_short_fleet,
  CAPTAIN: S.tab_short_captain, ECONOMY: S.tab_short_economy, WAR: S.tab_short_war,
};

export const tabShort = (key: string): string => TAB_SHORT[key] ?? tabLabel(key);

/** The two pages of ECONOMY · AUCTION. */
export const AUCTION_PAGES = ['BROWSE', 'ADD OFFER'] as const;

/**
 * A battle plan travels as ONE wire number (R796): the position in the low
 * byte and the behaviour in the next one. The shift is a wire encoding and
 * not a game figure, so it is stated here and the `.proto` comment is the
 * contract both sides read.
 */
export const PLAN_BEH_SHIFT = 8;

export const PLAN_POS_MASK = (1 << PLAN_BEH_SHIFT) - 1;

/**
 * The two pickers, each `[wire value, label]` in the order the panel draws
 * them. The wire values are the server's own enum (`sim.BattlePosition`
 * and `sim.BattleBehaviour`), where 0 is CENTRE and 0 is ENGAGE — the plan
 * a fleet nobody gave one fights on. The panel draws FRONT first, because
 * a reader reads the line from the front.
 */
export const PLAN_POSITIONS: [number, string][] = [
  [1, S.btn_plan_front], [0, S.btn_plan_centre], [2, S.btn_plan_rear],
];

export const PLAN_BEHAVIOURS: [number, string][] = [
  [0, S.btn_plan_engage], [1, S.btn_plan_hold], [2, S.btn_plan_flank],
];

/**
 * Prospecting's slot in the six attributes. The order is the server's own
 * (`AttrGunnery` and the five that follow it in `game/captains.go`), and the
 * catalog ships the names in that order, so the index is a contract of the
 * wire and not a figure of the pack.
 */
export const ATTR_PROSPECTING = 5;

/** The counter web's six role words (numbers.md §4, R407), keyed by the
 * pack word the catalog ships on every hull and every matchup. ROLE_ONE
 * names a single hull under its name; ROLE_MANY fills the strong and
 * weak lines, which count many hulls. The client mirrors no matrix — a
 * role word it does not know prints nothing at all. */
export const ROLE_ONE: Record<string, string> = {
  skirmish: S.role_skirmish, brawler: S.role_brawler, artillery: S.role_artillery,
  torpedo: S.role_torpedo, tower: S.role_tower, command: S.role_command, support: S.role_support,
};

export const ROLE_MANY: Record<string, string> = {
  skirmish: S.roles_skirmish, brawler: S.roles_brawler, artillery: S.roles_artillery,
  torpedo: S.roles_torpedo, tower: S.roles_tower, command: S.roles_command, support: S.roles_support,
};

/** The rungs both clock ladders offer, in multiples of wall time. The top
 * rung is the server's own clamp (hub/admin.go `clampScale`) — a
 * fast-forward, not a fidelity setting (R94).
 *
 * Two rungs are round numbers on purpose. 60x is one game-minute a wall
 * second and 3600x is one game-hour a wall second, so a tester who reads a
 * deadline in game time can say how long the wait is without arithmetic.
 * That is what makes a strategic object's vulnerability window watchable
 * inside one playtest.
 *
 * The world clock holds 14x and the map clock holds 60x: they are the
 * clocks of the event worlds (`deploy/fleet/dev.toml`), so the panel can
 * mark them in force and set them back (R1777). 60 is a rung of both
 * ladders; the map clock held a rung of 90 while the event map clock was
 * 90x, and it dropped that rung at 60x (R2416).
 *
 * A rung button carries the bare number. The row's own label states the
 * unit once (`world clock · 120x`), and eight buttons that each repeat the
 * `x` do not fit one row. */
export const CLOCK_LADDER = [1, 14, 30, 60, 120, 300, 600, 3600];

/** The map clock's ladder: `CLOCK_LADDER` without 14x (R1777, R2416). */
export const MAP_CLOCK_LADDER = [1, 30, 60, 120, 300, 600, 3600];

/** The advance-time row: how far one press pushes the world clock, in world
 * seconds (R183). An hour reaches the next attack lock, six hours reaches
 * the evening windows from a midday start, and a day reaches tomorrow's.
 *
 * A press moves the schedule and runs nothing — see `hub.warpTo`. */
export const ADVANCE_STEPS: number[] = [3600, 6 * 3600, 24 * 3600];

/**
 * The hint line of each armed pick mode, `[mode, for a pointer, for a
 * finger]`. The first armed row wins, and DEPLOY reads first. The line
 * names no key: the CANCEL press beside it carries the esc key cap (R2164).
 */
export const PICK_HINTS: [PickMode, string, string][] = [
  ['deploying', S.hint_deploy, S.hint_deploy_touch],
  ['escorting', S.hint_escort, S.hint_escort_touch],
  ['patrolling', S.hint_patrol, S.hint_patrol_touch],
  ['hunting', S.hint_hunt, S.hint_hunt_touch],
  ['moving', S.hint_move, S.hint_move_touch],
  ['gathering', S.hint_gather, S.hint_gather_touch],
  ['searching', S.hint_search, S.hint_search_touch],
];

/** The order word of each pick mode on the armed chip of a card (R2164). */
const PICK_ORDERS: Record<PickMode, string> = {
  deploying: CS.o_move, escorting: CS.o_escort, patrolling: CS.o_patrol,
  hunting: CS.o_hunt, moving: CS.o_move, gathering: CS.o_gather,
  searching: CS.o_search,
};

/**
 * The words of one armed order (R2164): the order word of the chip and
 * the line of the next map press, in touch words for a finger. `aim` is a
 * hull's shot and `aimbase` the shot of the hold's battery. The season
 * overlay and the arcade card both read it, so the two say one thing.
 * `vents` is a world whose map has vents: the gather line names them, as
 * the gather pick takes them (R3147).
 */
export function armWords(
  mode: PickMode | 'aim' | 'aimbase', touch: boolean, vents = false,
): { order: string; line: string; aim: boolean } {
  if (mode === 'aim' || mode === 'aimbase') {
    const base = mode === 'aimbase';
    return {
      order: CS.o_fire, aim: true,
      line: base ? (touch ? S.hint_aim_base_touch : S.hint_aim_base) : (touch ? S.hint_aim_touch : S.hint_aim),
    };
  }
  if (mode === 'gathering' && vents) {
    return { order: PICK_ORDERS[mode], aim: false, line: touch ? S.hint_gather_vent_touch : S.hint_gather_vent };
  }
  const row = PICK_HINTS.find(([m]) => m === mode);
  return { order: PICK_ORDERS[mode], aim: false, line: row ? (touch ? row[2] : row[1]) : '' };
}

/** The acts that arm one pick mode (R469). `gatherpick` and not `gather`:
 * the deposit panel's SEND HAULER already holds that name, and it orders a
 * named field rather than arming a pick. */
export const PICK_ACTS: Record<string, PickMode> = {
  escort: 'escorting', patrol: 'patrolling', hunt: 'hunting',
  move: 'moving', gatherpick: 'gathering', deploystart: 'deploying',
  // SEARCH of the ⋯ of a fit hull (v2 §9.6, R2834).
  searchpick: 'searching',
};

/** The acts that send one fleet op for the hull in `data-id` and no more. */
export const FLEET_OP_ACTS: Record<string, FleetOp> = {
  dock: FleetOp.FLEET_OP_DOCK, return: FleetOp.FLEET_OP_RETURN,
  anchor: FleetOp.FLEET_OP_ANCHOR, scan: FleetOp.FLEET_OP_SCAN,
  share: FleetOp.FLEET_OP_SHARE_SONAR, deeprun: FleetOp.FLEET_OP_DEEP_RUN,
  decoy: FleetOp.FLEET_OP_DECOY, autofield: FleetOp.FLEET_OP_AUTO_FIELD,
  holdfire: FleetOp.FLEET_OP_HOLD_FIRE,
  // The signature order of an own hull, spent now (R2376, `ui/cards/approach.ts` SIG_ACT).
  signature: FleetOp.FLEET_OP_SIGNATURE,
  // The beam order of a SET Beacon (R2858, `ui/cards/beam.ts` BEAM_ACT).
  beam: FleetOp.FLEET_OP_BEAM,
  // COLLECT of a Sapper (v2 §11.1, R2651); LAY carries a kind, so the
  // overlay sends it and keeps the last kind (`ui/cards/sapper.ts`).
  collect: FleetOp.FLEET_OP_COLLECT,
};

/**
 * The acts that send one intent and do nothing else in the chrome, with
 * the id and the count of their press. `actButton` dispatches them before
 * its switch, as it dispatches the rows below.
 */
export const INTENT_ACTS: Readonly<Record<string, (a: UIActions, id: number, n: number) => void>> = {
  build: (a, id) => a.build(id),
  ship: (a, id, n) => a.buildShips(id, n),
  torp: (a, id, n) => a.buildTorpedoes(id, n),
  // ORDER of a kind of sea object on BUILD · DEPOT (R2653).
  seaobj: (a, id, n) => a.orderSeaObjects(id, n),
  research: (a, id) => a.research(id),
  // Touch chrome: the two escapes a finger has no key or button for.
  deselect: (a) => a.deselect(),
  cancelmodes: (a) => a.cancelModes(),
  // Back to the hub (R236). It disarms LOG OUT on the way out: a player
  // who armed it and then chose MENU did not choose to sign out, and an
  // armed button must never survive the screen it stood on.
  menu: (a) => a.menu(),
  // ARCADE stands beside MENU in the session block (R1731): the same
  // way out of the world, onto the arcade page.
  arcade: (a) => a.arcade(),
  // Two presses, no modal. The first press only changes the label; the
  // second one, inside a few seconds, ends the session. A press that
  // arrives after the timer has run finds the button at rest and arms
  // it again rather than logging anybody out.
  logout: (a) => a.logout(),
  // A press on a mode of the heat page (round 086 card 10): it paints
  // the mode, and a second press on the active mode turns it off
  // (`actions.ts setHeatmap`). The page stays, so the check moves.
  heatset: (a, id) => a.setHeatmap(id),
  mtrack: (a, _id, n) => a.audioOp('jump', n),
  relcap: (a, id, n) => a.releaseCaptain(id, n),
  rush: (a, id) => a.rushJob(id),
  cancel: (a, id) => a.cancelJob(id),
  qmove: (a, id, n) => a.moveJob(id, n),
  deploy: (a, id, n) => a.deploy(id, n, 0),
  // A pill of the round pick of the own fleet card (R2031): the id is
  // "fleet:kind". The reload runs on, and the next shot fires it.
  setround: (a, id, n) => a.fleetOp(id, FleetOp.FLEET_OP_SET_ROUND, n),
  breakfleet: (a, id) => a.fleetOp(id, FleetOp.FLEET_OP_BREAK, 0),
  install: (a, id) => a.installPrint(id),
  scrap: (a, id) => a.scrapDesign(id),
  redraft: (a, id, n) => a.redraft(id, n),
  mbuy: (a, id) => a.market({ buy: id }),
  mcancel: (a, id) => a.market({ cancel: id }),
  // One handler for both picker rows: `data-n` is the whole packed
  // battle plan (R796).
  setplan: (a, id, n) => a.fleetOp(id, FleetOp.FLEET_OP_SET_BATTLE_PLAN, n),
  pick: (a, id) => a.selectFleet(id),
  repair: (a, id) => a.repair(id),
  mission: (a, id, n) => a.setMission(id, n),
  recruit: (a, id) => a.recruit(id),
  // RELEASE is armed on the first press and acts on the second (R696).
  // The PASS of a cadet carries no `data-arm`, so it acts at once.
  dismiss: (a, id) => a.dismiss(id),
  ransom: (a, id) => a.ransom(id),
  gather: (a, id, n) => a.gatherAt(id, n),
};

/** The media player's acts that send one transport op with no argument. */
export const AUDIO_ACTS: Record<string, AudioOp> = {
  mplay: 'play', mpause: 'pause', mstop: 'stop', mnext: 'next', mprev: 'prev',
};

/**
 * The acts a two-press gate can stand before (R696, R837). A button of one
 * of these acts that carries `data-arm` arms on the first press and acts on
 * the second. The CASINO panel is not here: it owns its own gate calls.
 */
export const ARMED_ACTS: ReadonlySet<string> = new Set([
  'logout', 'relcap', 'adm', 'cancel', 'breakfleet', 'install', 'scrap', 'dismiss', 'plotdel',
]);

// Widest audience first, and SQUAD before CLAN: a squad is the group a
// player talks to most, so it sits closer to the input line than the clan
// tab does. Nothing else reads this order.
export const CHANNELS: [string, ChatChannel][] = [
  [S.channels[0], ChatChannel.CHAT_CHANNEL_GLOBAL],
  [S.channels[1], ChatChannel.CHAT_CHANNEL_FACTION],
  [S.channels[2], ChatChannel.CHAT_CHANNEL_SQUAD],
  [S.channels[3], ChatChannel.CHAT_CHANNEL_CLAN],
];
